import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Mail, ExternalLink, X, AlertTriangle, BarChart2, GitBranch, Clock, CheckCircle2 } from 'lucide-react';
import { JourneyNode as JourneyNodeType } from '../../services/api';

export const NODE_WIDTH = 264;
export const NODE_HEIGHT = 116; // base (no analytics)
export const ANALYTICS_EXTRA = 90;

interface AnalyticsData {
    enrolled: number;
    active: number;
    paused: number;
    completed: number;
    total_sent: number;
    open_rate: number;
    click_rate: number;
}

interface Props {
    node: JourneyNodeType;
    allNodes: JourneyNodeType[];
    isSelected: boolean;
    isConcurrent: boolean; // target of any halt_source=false edge
    hasMultipleHalt: boolean; // has 2+ halt_source=true outgoing edges
    analyticsData?: AnalyticsData;
    showAnalytics: boolean;
    onConnectorPointerDown: (e: React.PointerEvent, nodeId: string) => void;
    onUnlink: (nodeId: string) => void;
    onEditEDM: (sequenceId: string) => void;
}

export default function JourneyNode({
    node, allNodes, isSelected, isConcurrent, hasMultipleHalt,
    analyticsData, showAnalytics,
    onConnectorPointerDown, onUnlink, onEditEDM,
}: Props) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: node.id });

    const style: React.CSSProperties = {
        left: (node.position?.x ?? 0),
        top: (node.position?.y ?? 0),
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.7 : 1,
    };

    const nodeType = (node as any).type ?? 'sequence';
    const seq = node.edm_sequence;

    const statusClass = seq ? `journey-node-status-dot--${seq.status}` : 'journey-node-status-dot--draft';

    // ── A/B Split node ──────────────────────────────────────────────────────
    if (nodeType === 'ab_split') {
        const splits: Array<{ to_node: string; weight: number }> = (node as any).splits ?? [];
        return (
            <div
                ref={setNodeRef}
                style={style}
                className={`journey-node journey-node--ab-split${isSelected ? ' journey-node--selected' : ''}${isDragging ? ' journey-node--dragging' : ''}`}
            >
                <div className="journey-node-body" {...listeners} {...attributes}>
                    <div className="journey-node-header">
                        <GitBranch size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                        <span className="journey-node-name">A/B Split</span>
                    </div>
                    <div className="journey-node-meta">{splits.length} branches</div>
                </div>
                {splits.length > 0 && (
                    <div className="journey-ab-split-rows">
                        {splits.map((s, i) => {
                            const target = allNodes.find(n => n.id === s.to_node);
                            return (
                                <div key={i} className="journey-ab-split-row">
                                    <span className="journey-ab-split-weight">{s.weight}%</span>
                                    <span className="journey-ab-split-target">
                                        → {target?.edm_sequence?.name ?? target?.id ?? '—'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="journey-connector-wrap">
                    <div
                        className="journey-connector-handle"
                        onPointerDown={e => onConnectorPointerDown(e, node.id)}
                    />
                </div>
            </div>
        );
    }

    // ── Wait node ────────────────────────────────────────────────────────────
    if (nodeType === 'wait') {
        const delayDays: number = (node as any).delay_days ?? 1;
        return (
            <div
                ref={setNodeRef}
                style={style}
                className={`journey-node journey-node--wait${isSelected ? ' journey-node--selected' : ''}${isDragging ? ' journey-node--dragging' : ''}`}
            >
                <div className="journey-node-body" {...listeners} {...attributes}>
                    <div className="journey-node-header">
                        <Clock size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                        <span className="journey-node-name">Wait</span>
                        <button
                            className="journey-node-action-btn journey-node-action-btn--danger"
                            onClick={e => { e.stopPropagation(); onUnlink(node.id); }}
                            title="Remove wait node"
                        >
                            <X size={12} />
                        </button>
                    </div>
                    <div className="journey-wait-delay">
                        <Clock size={14} />
                        {delayDays} day{delayDays !== 1 ? 's' : ''}
                    </div>
                </div>
                <div className="journey-connector-wrap">
                    <div
                        className="journey-connector-handle"
                        onPointerDown={e => onConnectorPointerDown(e, node.id)}
                    />
                </div>
            </div>
        );
    }

    // ── Goal node ────────────────────────────────────────────────────────────
    if (nodeType === 'goal') {
        const convType: string = (node as any).conversion_type ?? 'purchase';
        return (
            <div
                ref={setNodeRef}
                style={style}
                className={`journey-node journey-node--goal${isSelected ? ' journey-node--selected' : ''}${isDragging ? ' journey-node--dragging' : ''}`}
            >
                <div className="journey-node-body" {...listeners} {...attributes}>
                    <div className="journey-node-header">
                        <CheckCircle2 size={16} style={{ color: '#22c55e', flexShrink: 0 }} />
                        <span className="journey-node-name">Goal</span>
                        <button
                            className="journey-node-action-btn journey-node-action-btn--danger"
                            onClick={e => { e.stopPropagation(); onUnlink(node.id); }}
                            title="Remove goal node"
                        >
                            <X size={12} />
                        </button>
                    </div>
                    <div className="journey-node-meta">Trigger: {convType}</div>
                </div>
                {/* Goal nodes have no outgoing connector */}
            </div>
        );
    }

    // ── Sequence node (default) ──────────────────────────────────────────────
    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`journey-node${isSelected ? ' journey-node--selected' : ''}${isDragging ? ' journey-node--dragging' : ''}`}
        >
            <div className="journey-node-body" {...listeners} {...attributes}>
                <div className="journey-node-header">
                    <Mail size={15} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                    <span className="journey-node-name">{seq?.name ?? node.edm_sequence_id}</span>
                    <div className={`journey-node-status-dot ${statusClass}`} title={seq?.status} />
                </div>

                <div className="journey-node-meta">
                    {seq?.step_count ?? 0} step{seq?.step_count !== 1 ? 's' : ''}
                    {analyticsData && showAnalytics && (
                        <span>· {analyticsData.enrolled} enrolled</span>
                    )}
                    {isConcurrent && (
                        <span className="journey-concurrent-badge">
                            ∥ concurrent
                        </span>
                    )}
                </div>

                <div className="journey-node-actions">
                    {seq && (
                        <button
                            className="journey-node-action-btn"
                            onClick={e => { e.stopPropagation(); onEditEDM(seq.sequence_id); }}
                            title="Edit in EDM"
                        >
                            <ExternalLink size={12} />
                        </button>
                    )}
                    <button
                        className="journey-node-action-btn journey-node-action-btn--danger"
                        onClick={e => { e.stopPropagation(); onUnlink(node.id); }}
                        title="Remove from journey"
                    >
                        <X size={12} />
                    </button>
                </div>
            </div>

            {/* Multi-halt conflict warning */}
            {hasMultipleHalt && (
                <div className="journey-multi-halt-warning">
                    <AlertTriangle size={12} style={{ flexShrink: 0 }} />
                    Multiple halt-triggers. First match wins.
                </div>
            )}

            {/* Analytics overlay */}
            {showAnalytics && analyticsData && (
                <div className="journey-node-analytics">
                    <div className="journey-node-analytics-stat">
                        <BarChart2 size={11} />
                        Sent: <strong>{analyticsData.total_sent}</strong>
                    </div>
                    <div className="journey-node-analytics-stat">
                        Opens: <strong>{analyticsData.open_rate.toFixed(0)}%</strong>
                    </div>
                    <div className="journey-node-analytics-stat">
                        Clicks: <strong>{analyticsData.click_rate.toFixed(0)}%</strong>
                    </div>
                    <div className="journey-node-analytics-stat">
                        ✓ Done: <strong>{analyticsData.completed}</strong>
                    </div>
                    <div className="journey-node-analytics-stat">
                        ⏸ Paused: <strong>{analyticsData.paused}</strong>
                    </div>
                </div>
            )}

            {/* Connector handle */}
            <div className="journey-connector-wrap">
                <div
                    className="journey-connector-handle"
                    onPointerDown={e => onConnectorPointerDown(e, node.id)}
                    title="Drag to connect to another node"
                />
            </div>
        </div>
    );
}
